import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { Skeleton } from "./skeleton";

interface OptimizedImageProps {
  src: string;
  alt: string;
  preloadedSrc?: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  aspectRatio?: string;
  showSkeleton?: boolean;
  onLoad?: () => void;
}

export const OptimizedImage = ({ 
  src, 
  alt, 
  preloadedSrc,
  className,
  loading = 'lazy',
  aspectRatio,
  showSkeleton = true,
  onLoad
}: OptimizedImageProps) => {
  const [isLoaded, setIsLoaded] = useState(!!preloadedSrc);
  const [imageSrc, setImageSrc] = useState<string>(preloadedSrc || src);
  const imgRef = useRef<HTMLImageElement>(null);
  const loadedSrcRef = useRef<string | null>(preloadedSrc || null);

  useEffect(() => {
    // Once loaded with a preloadedSrc, don't regress to unloaded state
    if (preloadedSrc && preloadedSrc !== loadedSrcRef.current) {
      setImageSrc(preloadedSrc);
      loadedSrcRef.current = preloadedSrc;
      setIsLoaded(true);
      onLoad?.();
    } else if (!preloadedSrc && src !== imageSrc && !loadedSrcRef.current) {
      setImageSrc(src);
    }
  }, [src, preloadedSrc]);

  const handleLoad = () => {
    if (!isLoaded) {
      setIsLoaded(true);
      loadedSrcRef.current = imageSrc;
      onLoad?.();
    }
  };

  return (
    <div 
      className={cn("relative overflow-hidden", className)}
      style={aspectRatio ? { aspectRatio } : undefined}
    >
      {showSkeleton && !isLoaded && (
        <Skeleton className="absolute inset-0 w-full h-full" />
      )}
      <img
        ref={imgRef}
        src={imageSrc}
        alt={alt}
        loading={loading}
        decoding="async"
        onLoad={handleLoad}
        className={cn(
          "w-full h-full object-cover transition-opacity duration-300",
          isLoaded ? "opacity-100" : "opacity-0"
        )}
      />
    </div>
  );
};
